import { memo, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  useHasAnalyticsDashboardsBills,
  useHasAnalyticsDashboardsPurchases,
  useHasAnalyticsDashboardsSales,
} from '~/hooks/useFeatureFlag';
import useRouteMatch from '~/hooks/useRouteMatch';
import AnalyticsIcon from '~/icons/icon-chart-up.svg?react';
import { RouteConfig } from '~/routes/routeConfig';
import { useIsSubmenuOpen, useSetSubmenu } from '~/state/navigationSidebar';
import { UserRole } from '~/types/company';
import { SubmenuItem, SubmenuSection } from '~/types/sidebar';
import NavigationSidebarItem from '../NavigationSidebarItem';

const AnalyticsItem: React.FC = () => {
  const { t } = useTranslation('sidebar', { keyPrefix: 'analytics' });
  const hasPurchases = useHasAnalyticsDashboardsPurchases([UserRole.ENTREPRENEUR]);
  const hasSales = useHasAnalyticsDashboardsSales([UserRole.ENTREPRENEUR]);
  const hasBills = useHasAnalyticsDashboardsBills([UserRole.ENTREPRENEUR]);
  const canAccess = hasPurchases || hasSales || hasBills;
  const isSubmenuOpen = useIsSubmenuOpen('analytics');
  const openSubmenu = useSetSubmenu('analytics');

  const sections: SubmenuSection[] = useMemo(() => {
    const reportsItems: SubmenuItem[] = [];

    if (hasPurchases) {
      reportsItems.push({
        label: t('submenu.purchases'),
        href: RouteConfig.AnalyticsPurchases.buildLink(),
        matchSubRoutes: true,
      });
    }

    if (hasSales) {
      reportsItems.push({
        label: t('submenu.sales'),
        href: RouteConfig.AnalyticsSales.buildLink(),
        matchSubRoutes: true,
      });
    }

    if (hasBills) {
      reportsItems.push({
        label: t('submenu.bills'),
        href: RouteConfig.AnalyticsBills.buildLink(),
        matchSubRoutes: true,
      });
    }

    return [{ label: t('submenu.available_reports'), items: reportsItems }];
  }, [t, hasPurchases, hasSales, hasBills]);

  const isActive = Boolean(useRouteMatch(sections.flatMap((s) => s.items.map((i) => i.href ?? ''))));

  if (!canAccess) return null;

  return (
    <NavigationSidebarItem
      href={RouteConfig.Analytics.buildLink()}
      iconComponent={AnalyticsIcon}
      title={t('label')}
      isActive={isActive}
      isSubmenuOpen={isSubmenuOpen}
      onOpenSubmenu={openSubmenu}
      submenuSections={sections}
    />
  );
};

export default memo(AnalyticsItem);
